export interface SectorWeight {
  symbol: string;
  weight: number; // 0 to 1
}

export interface RegimeAllocation {
  equities: number;
  bonds: number;
  commodities: number;
  cash: number;
  description: {
    ko: string;
    en: string;
  };
  sectors: SectorWeight[];
}

export type MarketRegime = 'Goldilocks' | 'Reflation' | 'Stagflation' | 'Deflation' | 'Neutral';

export const US_REGIME_ALLOCATIONS: Record<MarketRegime, RegimeAllocation> = {
  Goldilocks: {
    equities: 0.70,
    bonds: 0.20,
    commodities: 0.05,
    cash: 0.05,
    description: {
      ko: '저물가 고성장 국면입니다. 기술주와 성장주 비중을 확대하고 채권 비중을 유지하는 전략이 유효합니다.',
      en: 'Low inflation and high growth. Strategy: Overweight technology and growth stocks, maintain bond exposure.'
    },
    sectors: [
      { symbol: 'XLK', weight: 0.30 }, // Technology
      { symbol: 'XLY', weight: 0.15 }, // Consumer Discretionary
      { symbol: 'XLC', weight: 0.15 }, // Communication Services
      { symbol: 'XLF', weight: 0.10 }, // Financials
      { symbol: 'XLI', weight: 0.10 }, // Industrials
      { symbol: 'XLV', weight: 0.05 }, // Health Care
      { symbol: 'XLP', weight: 0.05 }, // Consumer Staples
      { symbol: 'XLB', weight: 0.05 }, // Materials
      { symbol: 'XLE', weight: 0.02 }, // Energy
      { symbol: 'XLU', weight: 0.02 }, // Utilities
      { symbol: 'XLRE', weight: 0.01 }, // Real Estate
    ],
  },
  Reflation: {
    equities: 0.60,
    bonds: 0.10,
    commodities: 0.30,
    cash: 0.0,
    description: {
      ko: '물가와 성장이 동시에 상승하는 국면입니다. 원자재, 에너지, 금융주 등 경기 민감 섹터가 유리합니다.',
      en: 'Rising inflation and growth. Strategy: Favors commodities, energy, financials, and cyclical sectors.'
    },
    sectors: [
      { symbol: 'XLE', weight: 0.20 }, // Energy
      { symbol: 'XLF', weight: 0.20 }, // Financials
      { symbol: 'XLB', weight: 0.15 }, // Materials
      { symbol: 'XLI', weight: 0.15 }, // Industrials
      { symbol: 'XLK', weight: 0.05 }, // Technology
      { symbol: 'XLY', weight: 0.05 }, // Consumer Discretionary
      { symbol: 'XLV', weight: 0.05 }, // Health Care
      { symbol: 'XLC', weight: 0.05 }, // Communication Services
      { symbol: 'XLP', weight: 0.05 }, // Consumer Staples
      { symbol: 'XLU', weight: 0.02 }, // Utilities
      { symbol: 'XLRE', weight: 0.03 }, // Real Estate
    ],
  },
  Stagflation: {
    equities: 0.30,
    bonds: 0.20,
    commodities: 0.30,
    cash: 0.20,
    description: {
      ko: '고물가 저성장 국면입니다. 현금 비중을 높이고 필수소비재, 헬스케어 등 방어주와 원자재 비중을 확대하세요.',
      en: 'High inflation and low growth. Strategy: Increase cash, overweight defensive sectors (staples, healthcare) and commodities.'
    },
    sectors: [
      { symbol: 'XLE', weight: 0.26 }, // Energy (+0.01)
      { symbol: 'XLP', weight: 0.21 }, // Consumer Staples (+0.01)
      { symbol: 'XLV', weight: 0.15 }, // Health Care
      { symbol: 'XLU', weight: 0.15 }, // Utilities
      { symbol: 'XLB', weight: 0.05 }, // Materials
      { symbol: 'XLRE', weight: 0.05 }, // Real Estate
      { symbol: 'XLF', weight: 0.05 }, // Financials
      { symbol: 'XLI', weight: 0.02 }, // Industrials
      { symbol: 'XLK', weight: 0.02 }, // Technology
      { symbol: 'XLY', weight: 0.02 }, // Consumer Discretionary
      { symbol: 'XLC', weight: 0.02 }, // Communication Services
    ],
  },
  Deflation: {
    equities: 0.40,
    bonds: 0.40,
    commodities: 0.05,
    cash: 0.15,
    description: {
      ko: '물가와 성장이 동시에 하락하는 국면입니다. 채권 비중을 대폭 확대하고 유틸리티, 헬스케어 등 경기 방어주 중심의 포트폴리오를 구성하세요.',
      en: 'Falling inflation and growth. Strategy: Significantly increase bond exposure, focus on defensive sectors like utilities and healthcare.'
    },
    sectors: [
      { symbol: 'XLU', weight: 0.20 }, // Utilities
      { symbol: 'XLV', weight: 0.20 }, // Health Care
      { symbol: 'XLP', weight: 0.20 }, // Consumer Staples
      { symbol: 'XLRE', weight: 0.10 }, // Real Estate
      { symbol: 'XLK', weight: 0.05 }, // Technology
      { symbol: 'XLF', weight: 0.05 }, // Financials
      { symbol: 'XLC', weight: 0.05 }, // Communication Services
      { symbol: 'XLI', weight: 0.05 }, // Industrials
      { symbol: 'XLB', weight: 0.02 }, // Materials
      { symbol: 'XLE', weight: 0.03 }, // Energy
      { symbol: 'XLY', weight: 0.05 }, // Consumer Discretionary
    ],
  },
  Neutral: {
    equities: 0.50,
    bonds: 0.30,
    commodities: 0.10,
    cash: 0.10,
    description: {
      ko: '시장 방향성이 불분명한 중립 국면입니다. 균형 잡힌 자산 배분과 섹터 다변화 전략이 필요합니다.',
      en: 'Market direction unclear. Strategy: Balanced asset allocation and broad sector diversification.'
    },
    sectors: [
      { symbol: 'XLK', weight: 0.10 },
      { symbol: 'XLF', weight: 0.10 },
      { symbol: 'XLV', weight: 0.10 },
      { symbol: 'XLI', weight: 0.10 },
      { symbol: 'XLY', weight: 0.10 },
      { symbol: 'XLP', weight: 0.10 },
      { symbol: 'XLC', weight: 0.10 },
      { symbol: 'XLU', weight: 0.10 },
      { symbol: 'XLB', weight: 0.05 },
      { symbol: 'XLE', weight: 0.05 },
      { symbol: 'XLRE', weight: 0.10 }, // (+0.05)
    ],
  },
};

export const KR_REGIME_ALLOCATIONS: Record<MarketRegime, RegimeAllocation> = {
  Goldilocks: {
    equities: 0.75,
    bonds: 0.15,
    commodities: 0.05,
    cash: 0.05,
    description: {
      ko: '한국 시장 골디락스 국면입니다. 반도체, 이차전지 등 성장 섹터 비중을 극대화하세요.',
      en: 'KR Goldilocks. Strategy: Maximize exposure to growth sectors like Semiconductors and Secondary Batteries.'
    },
    sectors: [
      { symbol: '091160.KS', weight: 0.35 }, // Semiconductors
      { symbol: '305710.KS', weight: 0.20 }, // Secondary Battery
      { symbol: '229200.KS', weight: 0.15 }, // Game
      { symbol: '244580.KS', weight: 0.10 }, // Bio
      { symbol: '091170.KS', weight: 0.10 }, // Automotive
      { symbol: '091180.KS', weight: 0.10 }, // Financials (+0.05)
    ],
  },
  Reflation: {
    equities: 0.65,
    bonds: 0.05,
    commodities: 0.30,
    cash: 0.0,
    description: {
      ko: '수출 회복과 물가 상승이 동반되는 국면입니다. 금융, 자동차 등 가치주와 경기 민감주가 유망합니다.',
      en: 'Rising exports and inflation. Strategy: Favors value and cyclical stocks like Financials and Automotive.'
    },
    sectors: [
      { symbol: '091180.KS', weight: 0.30 }, // Financials
      { symbol: '091170.KS', weight: 0.25 }, // Automotive
      { symbol: '091160.KS', weight: 0.15 }, // Semiconductors
      { symbol: '305710.KS', weight: 0.10 }, // Secondary Battery
      { symbol: '244580.KS', weight: 0.10 }, // Bio
      { symbol: '229200.KS', weight: 0.10 }, // Game
    ],
  },
  Stagflation: {
    equities: 0.35,
    bonds: 0.15,
    commodities: 0.30,
    cash: 0.20,
    description: {
      ko: '성장 정체와 물가 부담이 공존합니다. 바이오, 금융 등 방어적 성격의 배당주와 현금 비중을 확보하세요.',
      en: 'Stagnant growth and high inflation. Strategy: Increase cash and defensive dividend stocks like Bio and Financials.'
    },
    sectors: [
      { symbol: '091180.KS', weight: 0.30 }, // Financials (Defensive yield)
      { symbol: '244580.KS', weight: 0.25 }, // Bio (Defensive)
      { symbol: '091170.KS', weight: 0.15 }, // Automotive
      { symbol: '091160.KS', weight: 0.10 }, // Semiconductors
      { symbol: '305710.KS', weight: 0.10 }, // Secondary Battery
      { symbol: '229200.KS', weight: 0.10 }, // Game
    ],
  },
  Deflation: {
    equities: 0.45,
    bonds: 0.35,
    commodities: 0.0,
    cash: 0.20,
    description: {
      ko: '내수 부진과 물가 하락 국면입니다. 국고채 등 안전자산 비중을 높이고 필수재 성격의 바이오 섹터를 선호합니다.',
      en: 'Weak domestic demand and falling prices. Strategy: Increase safe assets like Treasury bonds, prefer Bio sector.'
    },
    sectors: [
      { symbol: '244580.KS', weight: 0.30 }, // Bio (Defensive)
      { symbol: '091180.KS', weight: 0.20 }, // Financials
      { symbol: '091160.KS', weight: 0.15 }, // Semiconductors
      { symbol: '091170.KS', weight: 0.15 }, // Automotive
      { symbol: '305710.KS', weight: 0.10 }, // Secondary Battery
      { symbol: '229200.KS', weight: 0.10 }, // Game
    ],
  },
  Neutral: {
    equities: 0.60,
    bonds: 0.30,
    commodities: 0.05,
    cash: 0.05,
    description: {
      ko: '추세가 불분명한 국면입니다. 반도체와 자동차 등 주도 섹터 중심의 포트폴리오를 유지하세요.',
      en: 'Unclear trend. Strategy: Maintain portfolio centered on leading sectors like Semiconductors and Automotive.'
    },
    sectors: [
      { symbol: '091160.KS', weight: 0.20 },
      { symbol: '091170.KS', weight: 0.20 },
      { symbol: '305710.KS', weight: 0.15 },
      { symbol: '091180.KS', weight: 0.15 },
      { symbol: '244580.KS', weight: 0.15 },
      { symbol: '229200.KS', weight: 0.15 },
    ],
  },
};

export const REGIME_ALLOCATIONS_MAP = {
  US: US_REGIME_ALLOCATIONS,
  KR: KR_REGIME_ALLOCATIONS,
};

export function getRegimeAllocation(regime: MarketRegime, marketMode: 'US' | 'KR' = 'US'): RegimeAllocation {
  const allocations = REGIME_ALLOCATIONS_MAP[marketMode] || US_REGIME_ALLOCATIONS;
  return allocations[regime] || allocations['Neutral'];
}

// Validation function to ensure weights sum to 1.0
export function validateAllocations() {
  const markets: ('US' | 'KR')[] = ['US', 'KR'];
  const regimes: MarketRegime[] = ['Goldilocks', 'Reflation', 'Stagflation', 'Deflation', 'Neutral'];

  markets.forEach(market => {
    regimes.forEach(regime => {
      const alloc = getRegimeAllocation(regime, market);
      
      const assetSum = alloc.equities + alloc.bonds + alloc.commodities + alloc.cash;
      if (Math.abs(assetSum - 1.0) > 0.001) {
        console.warn(`[Regime Validation] ${market} ${regime} assets sum to ${assetSum} (expected 1.0)`);
      }

      const sectorSum = alloc.sectors.reduce((sum, s) => sum + s.weight, 0);
      if (Math.abs(sectorSum - 1.0) > 0.001) {
        console.warn(`[Regime Validation] ${market} ${regime} sectors sum to ${sectorSum} (expected 1.0)`);
      }
    });
  });
}

// Run validation in development or on load
if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  validateAllocations();
}
